import { useRef, useState } from "react";
import type { Detail } from "./galleryTypes";
import { navigate, repoPath, useCurrentRepo } from "./router";
import { useAuthedAssetURL } from "./useAuthedAssetURL";
import { apiPath, useApiResource } from "./useApiResource";

const IMAGE_EXTS = new Set([".png", ".jpg", ".jpeg", ".gif"]);

// BitReader is the spec §6.5 B&IT reader/lightbox: zoom, full-screen
// preview, and revision grouping for images; an embedded (browser-native)
// viewer for everything else — real corpus PDFs open in the iframe's own
// built-in PDF renderer, since /original is served with
// Content-Disposition: inline specifically for this (see internal/gallery's
// package doc comment on the owner-confirmed PDF-thumbnail decision).
// Related ADRs/capabilities are omitted — no groundable signal exists for
// B&IT items (no fetched content, no sidecar metadata file in the real
// corpus).
export function BitReader({ token, id }: { token: string; id: string }) {
  const repo = useCurrentRepo();
  const { status, data, message } = useApiResource<Detail>(token, repo, `/bits/${encodeURIComponent(id)}`);
  const [zoomed, setZoomed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isImage = data ? IMAGE_EXTS.has(data.ext.toLowerCase()) : false;
  const assetPath = apiPath(repo, `/bits/${encodeURIComponent(id)}/original`);
  const { url } = useAuthedAssetURL(assetPath, token);

  const backLink = (
    <a
      href={repoPath(repo, "bits")}
      onClick={(e) => {
        e.preventDefault();
        navigate(repoPath(repo, "bits"));
      }}
    >
      ← Back to B&amp;IT gallery
    </a>
  );

  if (status === "loading") {
    return (
      <section className="panel">
        {backLink}
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready" || !data) {
    return (
      <section className="panel">
        {backLink}
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  const revisions = data.revisions ?? [];

  function toggleFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
    } else {
      void el.requestFullscreen?.();
    }
  }

  return (
    <section className="panel bit-reader">
      {backLink}
      <h2>{data.title}</h2>
      <p className="mono muted-note">
        {data.ext.replace(".", "").toUpperCase()}
        {data.isDraft ? " · Draft" : ""}
        {data.isWip ? " · WIP" : ""}
        {data.sector ? ` · ${data.sector}` : ""}
      </p>

      <div ref={containerRef} className="bit-preview">
        {url && isImage && (
          <>
            <img src={url} alt={data.title} className={zoomed ? "zoomed" : undefined} />
            <div className="bit-preview-controls">
              <button type="button" className="btn-ghost" onClick={() => setZoomed((z) => !z)}>
                {zoomed ? "Zoom out" : "Zoom in"}
              </button>
              <button type="button" className="btn-ghost" onClick={toggleFullscreen}>
                Full screen
              </button>
            </div>
          </>
        )}
        {url && !isImage && (
          <>
            {/* Real user report: some browsers render Content-Type:
                application/pdf blob: URLs inside an <iframe> as a blank box
                (likely the CSP's object-src 'none', which some browsers
                route PDF plugin rendering through even inside a frame — not
                reproducible headlessly, since jsdom doesn't render PDFs at
                all). A same-tab iframe embed is progressive enhancement;
                the link below is the primary, verified-working path: it
                opens the already-fetched blob in the browser's own
                full PDF viewer in a new tab, which every browser supports
                regardless of how it handles blob: content inside a frame. */}
            <div className="bit-preview-controls">
              <a className="btn-ghost" href={url} target="_blank" rel="noopener noreferrer">
                Open {data.ext.replace(".", "").toUpperCase()} in a new tab
              </a>
            </div>
            <iframe title={data.title} src={url} className="bit-embed" />
          </>
        )}
      </div>

      <h3>Revisions</h3>
      {revisions.length > 0 ? (
        <ul>
          {revisions
            .slice()
            .sort((a, b) => a.revision - b.revision)
            .map((r) => (
              <li key={r.id}>
                <a
                  href={repoPath(repo, "bits", r.id)}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(repoPath(repo, "bits", r.id));
                  }}
                >
                  {r.title}
                </a>
              </li>
            ))}
        </ul>
      ) : (
        <p className="muted-note">No other revisions found.</p>
      )}

      <p>
        <a href={data.sourceUrl} target="_blank" rel="noopener noreferrer">
          View source on GitHub
        </a>
      </p>
    </section>
  );
}
